import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '@/src/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { Textarea } from '@/src/components/ui/textarea';
import {
  Banknote,
  Wallet,
  IndianRupee,
  TrendingDown,
  TrendingUp,
  Download,
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
  Building2,
  Users,
  Bike,
  History,
  X,
  Clock,
  ArrowUpRight,
  ExternalLink,
  ShieldCheck,
  ChevronRight,
  Info
} from 'lucide-react';

export interface MerchantPayout {
  beneficiary_type: 'merchant';
  tenant_id: string;
  name: string;
  module: string;
  wallet_id: string | null;
  payable: number;
  gross_collected: number;
  platform_commission: number;
  paid_out: number;
  withdrawals_on_hold: number;
  bank: {
    holder?: string | null;
    account?: string | null;
    ifsc?: string | null;
    upi?: string | null;
  } | null;
  last_payout_at?: string | null;
}

export interface ProviderPayout {
  beneficiary_type: 'professional' | 'rider';
  provider_type: string;
  service_provider_id: string;
  tenant_id: string;
  name: string;
  phone?: string | null;
  merchant_name?: string | null;
  module?: string | null;
  wallet_id: string | null;
  payable: number;
  earned_online: number;
  commission_recoverable: number;
  paid_out: number;
  withdrawals_on_hold: number;
  bank: {
    holder?: string | null;
    account?: string | null;
    ifsc?: string | null;
    upi?: string | null;
  } | null;
  last_payout_at?: string | null;
}

export interface PayoutOverviewTotals {
  merchants_payable: number;
  providers_payable: number;
  providers_recoverable: number;
  platform_commission_earned: number;
  paid_out_total: number;
}

export interface PayoutHistoryItem {
  id: string;
  beneficiary_type: string;
  name: string;
  amount: number;
  method: string;
  reference: string;
  note?: string | null;
  paid_at: string;
}

export function PayoutsView() {
  const supabase = getSupabaseClient();

  // Loading & Error States
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Overview Data
  const [merchants, setMerchants] = useState<MerchantPayout[]>([]);
  const [providers, setProviders] = useState<ProviderPayout[]>([]);
  const [totals, setTotals] = useState<PayoutOverviewTotals>({
    merchants_payable: 0,
    providers_payable: 0,
    providers_recoverable: 0,
    platform_commission_earned: 0,
    paid_out_total: 0
  });

  // Active Tab: 'merchants' | 'professionals' | 'riders' | 'history'
  const [activeTab, setActiveTab] = useState<'merchants' | 'professionals' | 'riders' | 'history'>('merchants');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [onlyPayablePositive, setOnlyPayablePositive] = useState(false);

  // Pay Modal State
  const [payTarget, setPayTarget] = useState<(MerchantPayout | ProviderPayout) | null>(null);
  const [payAmount, setPayAmount] = useState<string>('');
  const [payMethod, setPayMethod] = useState<'bank_transfer' | 'upi' | 'cash' | 'other'>('bank_transfer');
  const [payReference, setPayReference] = useState('');
  const [payNote, setPayNote] = useState('');
  const [recordingPayout, setRecordingPayout] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Person History Drawer State
  const [drawerPerson, setDrawerPerson] = useState<(MerchantPayout | ProviderPayout) | null>(null);
  const [personHistory, setPersonHistory] = useState<PayoutHistoryItem[]>([]);
  const [loadingPersonHistory, setLoadingPersonHistory] = useState(false);

  // General History Tab State
  const [generalHistory, setGeneralHistory] = useState<PayoutHistoryItem[]>([]);
  const [loadingHistoryTab, setLoadingHistoryTab] = useState(false);

  // Load Overview Data from get_payout_overview
  const loadOverview = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabase.rpc('get_payout_overview');
      if (error) {
        console.error('get_payout_overview error:', error);
        setAuthError(error.message || 'Not authorized or failed to fetch payouts');
        toast.error(error.message || 'Failed to load payouts');
        return;
      }
      if (data) {
        setMerchants(data.merchants || []);
        setProviders(data.providers || []);
        if (data.totals) {
          setTotals({
            merchants_payable: Number(data.totals.merchants_payable || 0),
            providers_payable: Number(data.totals.providers_payable || 0),
            providers_recoverable: Number(data.totals.providers_recoverable || 0),
            platform_commission_earned: Number(data.totals.platform_commission_earned || 0),
            paid_out_total: Number(data.totals.paid_out_total || 0)
          });
        }
      }
    } catch (err: any) {
      console.error('loadOverview exception:', err);
      setAuthError(err.message || 'Failed to load payouts');
      toast.error(err.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Load General History for History Tab
  const loadGeneralHistory = useCallback(async () => {
    if (!supabase) return;
    setLoadingHistoryTab(true);
    try {
      const { data, error } = await supabase.rpc('get_payout_history', {
        p_wallet_id: null,
        p_limit: 100
      });
      if (error) {
        console.error('get_payout_history error:', error);
        toast.error(error.message || 'Failed to load payout history');
        return;
      }
      setGeneralHistory(data || []);
    } catch (err: any) {
      console.error('loadGeneralHistory exception:', err);
      toast.error(err.message || 'Failed to load payout history');
    } finally {
      setLoadingHistoryTab(false);
    }
  }, [supabase]);

  // Load Person Specific History for Slide-over Drawer
  const openPersonDrawer = async (person: MerchantPayout | ProviderPayout) => {
    setDrawerPerson(person);
    setPersonHistory([]);
    if (!person.wallet_id || !supabase) return;

    setLoadingPersonHistory(true);
    try {
      const { data, error } = await supabase.rpc('get_payout_history', {
        p_wallet_id: person.wallet_id,
        p_limit: 50
      });
      if (error) {
        console.warn('Person history error:', error);
      } else {
        setPersonHistory(data || []);
      }
    } catch (err: any) {
      console.error('Person history exception:', err);
    } finally {
      setLoadingPersonHistory(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadGeneralHistory();
    }
  }, [activeTab, loadGeneralHistory]);

  // Copy helper
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Open Pay Modal
  const openPayModal = (item: MerchantPayout | ProviderPayout) => {
    setPayTarget(item);
    setPayAmount(String(Math.max(0, item.payable)));
    setPayMethod(item.bank?.upi ? 'upi' : 'bank_transfer');
    setPayReference('');
    setPayNote('');
  };

  // Submit Payout via record_payout RPC
  const handleConfirmPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payTarget || !supabase) return;

    if (!payTarget.wallet_id) {
      toast.error('No wallet ID configured for this beneficiary');
      return;
    }

    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Please enter a valid amount greater than 0');
      return;
    }

    if (amt > payTarget.payable) {
      toast.error(`Amount (₹${amt.toFixed(2)}) cannot exceed payable balance (₹${payTarget.payable.toFixed(2)})`);
      return;
    }

    if (!payReference.trim()) {
      toast.error('Transfer reference (UTR / transaction id) is required');
      return;
    }

    setRecordingPayout(true);
    try {
      const { data, error } = await supabase.rpc('record_payout', {
        p_wallet_id: payTarget.wallet_id,
        p_amount: amt,
        p_method: payMethod,
        p_reference: payReference.trim(),
        p_note: payNote.trim() || null
      });

      if (error) {
        console.error('record_payout error:', error);
        toast.error(error.message || 'Failed to record payout');
        return;
      }

      toast.success(`Payout of ₹${amt.toLocaleString('en-IN', { minimumFractionDigits: 2 })} recorded for "${payTarget.name}"!`);
      setPayTarget(null);
      await loadOverview();
      if (activeTab === 'history') {
        await loadGeneralHistory();
      }
    } catch (err: any) {
      console.error('record_payout exception:', err);
      toast.error(err.message || 'Failed to record payout');
    } finally {
      setRecordingPayout(false);
    }
  };

  // Distinct modules for filter
  const availableModules = useMemo(() => {
    const set = new Set<string>();
    merchants.forEach(m => m.module && set.add(m.module));
    providers.forEach(p => p.module && set.add(p.module));
    return Array.from(set);
  }, [merchants, providers]);

  // Filtered Merchants
  const filteredMerchants = useMemo(() => {
    return merchants
      .filter(m => {
        const matchesSearch =
          m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.tenant_id.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesModule = selectedModule === 'all' || m.module === selectedModule;
        const matchesPayable = !onlyPayablePositive || m.payable > 0;
        return matchesSearch && matchesModule && matchesPayable;
      })
      .sort((a, b) => b.payable - a.payable);
  }, [merchants, searchQuery, selectedModule, onlyPayablePositive]);

  // Filtered Professionals
  const filteredProfessionals = useMemo(() => {
    return providers
      .filter(p => p.beneficiary_type === 'professional')
      .filter(p => {
        const matchesSearch =
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p.merchant_name && p.merchant_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (p.phone && p.phone.includes(searchQuery));
        const matchesModule = selectedModule === 'all' || p.module === selectedModule;
        const matchesPayable = !onlyPayablePositive || p.payable > 0;
        return matchesSearch && matchesModule && matchesPayable;
      })
      .sort((a, b) => b.payable - a.payable);
  }, [providers, searchQuery, selectedModule, onlyPayablePositive]);

  // Filtered Riders
  const filteredRiders = useMemo(() => {
    return providers
      .filter(p => p.beneficiary_type === 'rider')
      .filter(p => {
        const matchesSearch =
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p.merchant_name && p.merchant_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (p.phone && p.phone.includes(searchQuery));
        const matchesModule = selectedModule === 'all' || p.module === selectedModule;
        const matchesPayable = !onlyPayablePositive || p.payable > 0;
        return matchesSearch && matchesModule && matchesPayable;
      })
      .sort((a, b) => b.payable - a.payable);
  }, [providers, searchQuery, selectedModule, onlyPayablePositive]);

  // Filtered History
  const filteredHistory = useMemo(() => {
    return generalHistory.filter(h =>
      h.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      h.reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
      h.method.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [generalHistory, searchQuery]);

  // CSV Export for Visible Table
  const handleExportCSV = () => {
    let headers: string[] = [];
    let rows: (string | number)[][] = [];
    let filename = `payouts_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;

    if (activeTab === 'merchants') {
      headers = ['Business Name', 'Module', 'Gross Collected', 'Platform Commission', 'Paid Out', 'On Hold', 'Payable Now', 'Bank Account', 'IFSC'];
      rows = filteredMerchants.map(m => [
        m.name,
        m.module,
        m.gross_collected,
        m.platform_commission,
        m.paid_out,
        m.withdrawals_on_hold,
        m.payable,
        m.bank?.account || 'Missing',
        m.bank?.ifsc || 'Missing'
      ]);
    } else if (activeTab === 'professionals') {
      headers = ['Name', 'Merchant', 'Phone', 'Earned Online', 'Commission Recoverable', 'Paid Out', 'Payable Now', 'Bank Account / UPI', 'IFSC'];
      rows = filteredProfessionals.map(p => [
        p.name,
        p.merchant_name || '',
        p.phone || '',
        p.earned_online,
        p.commission_recoverable,
        p.paid_out,
        p.payable,
        p.bank?.upi || p.bank?.account || 'Missing',
        p.bank?.ifsc || ''
      ]);
    } else if (activeTab === 'riders') {
      headers = ['Name', 'Merchant', 'Phone', 'Earned Online', 'Commission Recoverable', 'Paid Out', 'Payable Now', 'Bank Account / UPI', 'IFSC'];
      rows = filteredRiders.map(r => [
        r.name,
        r.merchant_name || '',
        r.phone || '',
        r.earned_online,
        r.commission_recoverable,
        r.paid_out,
        r.payable,
        r.bank?.upi || r.bank?.account || 'Missing',
        r.bank?.ifsc || ''
      ]);
    } else if (activeTab === 'history') {
      headers = ['Date', 'Name', 'Beneficiary Type', 'Amount', 'Method', 'Reference', 'Note'];
      rows = filteredHistory.map(h => [
        new Date(h.paid_at).toLocaleString(),
        h.name,
        h.beneficiary_type,
        h.amount,
        h.method,
        h.reference,
        h.note || ''
      ]);
    }

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filename}`);
  };

  // Mask bank account number helper (e.g. ****1234)
  const maskAccount = (acc?: string | null) => {
    if (!acc) return null;
    const clean = acc.trim();
    if (clean.length <= 4) return clean;
    return '****' + clean.slice(-4);
  };

  // If Auth Error from RPC
  if (authError) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 flex items-center justify-center mx-auto">
          <AlertTriangle size={32} />
        </div>
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
          Access Restricted
        </h3>
        <p className="text-xs text-rose-600 dark:text-rose-400 font-mono bg-rose-50 dark:bg-rose-950/30 p-3 rounded-lg border border-rose-200 dark:border-rose-800">
          {authError}
        </p>
        <p className="text-xs text-slate-500">
          Only authorized platform super administrators can access this view.
        </p>
        <Button onClick={loadOverview} variant="outline" size="sm" className="gap-2 text-xs">
          <RefreshCw size={14} />
          <span>Retry</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2.5">
            <Wallet className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            <span>Payouts</span>
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Centrally manage and disburse balances payable to merchants, professionals, and riders.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="h-9 text-xs border-slate-200 dark:border-slate-700 gap-1.5"
          >
            <Download size={14} />
            <span>Download CSV</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadOverview}
            disabled={loading}
            className="h-9 text-xs border-slate-200 dark:border-slate-700 gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Task 6: Mandatory Info Note */}
      <div className="rounded-xl border border-sky-200 dark:border-sky-900/60 bg-sky-50/80 dark:bg-sky-950/30 p-4 text-xs text-sky-950 dark:text-sky-200 flex items-start gap-3 shadow-xs">
        <Info size={18} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          Online payments come to the BahiBox platform account. Amounts here are after platform commission and other deductions. Transfer manually, then record the payout.
        </p>
      </div>

      {/* Task 1: Summary Cards (5 Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
        {/* 1. Merchants Payable */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
              Payable to Merchants
            </span>
            <div className="text-xl font-extrabold font-mono text-slate-900 dark:text-white mt-1">
              ₹{totals.merchants_payable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 block">
              {merchants.filter(m => m.payable > 0).length} merchants waiting
            </span>
          </CardContent>
        </Card>

        {/* 2. Providers Payable */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
              Payable to Pros & Riders
            </span>
            <div className="text-xl font-extrabold font-mono text-slate-900 dark:text-white mt-1">
              ₹{totals.providers_payable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 block">
              Net balance payable
            </span>
          </CardContent>
        </Card>

        {/* 3. Recoverable from Professionals */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[10px] uppercase font-bold text-rose-500 tracking-wider block">
              Recoverable from Pros
            </span>
            <div className="text-xl font-extrabold font-mono text-rose-600 dark:text-rose-400 mt-1">
              ₹{totals.providers_recoverable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Commission owed from cash jobs
            </span>
          </CardContent>
        </Card>

        {/* 4. Platform Commission Earned */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[10px] uppercase font-bold text-indigo-500 tracking-wider block">
              Platform Commission
            </span>
            <div className="text-xl font-extrabold font-mono text-indigo-600 dark:text-indigo-400 mt-1">
              ₹{totals.platform_commission_earned.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Earned on collections
            </span>
          </CardContent>
        </Card>

        {/* 5. Total Paid Out */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
              Total Paid Out
            </span>
            <div className="text-xl font-extrabold font-mono text-slate-700 dark:text-slate-300 mt-1">
              ₹{totals.paid_out_total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-slate-400 mt-1 block">
              Disbursed to date
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-800 gap-6">
        <button
          onClick={() => setActiveTab('merchants')}
          className={`pb-3 text-xs font-bold transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'merchants'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Building2 size={16} />
          <span>Merchants ({merchants.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('professionals')}
          className={`pb-3 text-xs font-bold transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'professionals'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Users size={16} />
          <span>Professionals ({providers.filter(p => p.beneficiary_type === 'professional').length})</span>
        </button>

        <button
          onClick={() => setActiveTab('riders')}
          className={`pb-3 text-xs font-bold transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'riders'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <Bike size={16} />
          <span>Riders / Drivers ({providers.filter(p => p.beneficiary_type === 'rider').length})</span>
        </button>

        <button
          onClick={() => setActiveTab('history')}
          className={`pb-3 text-xs font-bold transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <History size={16} />
          <span>Payout History</span>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-3 flex-1">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <Input
              placeholder={activeTab === 'history' ? 'Search by name / UTR...' : 'Search name or tenant ID...'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>

          {/* Module Filter (for beneficiary tabs) */}
          {activeTab !== 'history' && (
            <select
              value={selectedModule}
              onChange={e => setSelectedModule(e.target.value)}
              className="h-9 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none"
            >
              <option value="all">All Modules</option>
              {availableModules.map(m => (
                <option key={m} value={m} className="capitalize">
                  {m}
                </option>
              ))}
            </select>
          )}

          {/* Only payable > 0 toggle */}
          {activeTab !== 'history' && (
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyPayablePositive}
                onChange={e => setOnlyPayablePositive(e.target.checked)}
                className="rounded border-slate-300 text-primary focus:ring-primary w-4 h-4"
              />
              <span>Only payable &gt; 0</span>
            </label>
          )}
        </div>

        <div className="text-xs text-slate-400">
          Sorted by Payable (high to low)
        </div>
      </div>

      {/* Main Content Area */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
            <RefreshCw size={24} className="animate-spin text-primary" />
            <span>Loading payout overview...</span>
          </div>
        ) : (
          <>
            {/* TAB 1: MERCHANTS */}
            {activeTab === 'merchants' && (
              <div className="overflow-x-auto">
                {filteredMerchants.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-xs">
                    No merchants match the selected filters.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase font-semibold text-[10px] text-slate-400 tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-3">Business</th>
                        <th className="px-4 py-3">Module</th>
                        <th className="px-4 py-3 text-right">Gross Collected</th>
                        <th className="px-4 py-3 text-right">Platform Comm</th>
                        <th className="px-4 py-3 text-right">Paid Out</th>
                        <th className="px-4 py-3 text-right">On Hold</th>
                        <th className="px-4 py-3 text-right">PAYABLE NOW</th>
                        <th className="px-4 py-3">Bank Details</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {filteredMerchants.map(m => (
                        <tr
                          key={m.tenant_id}
                          onClick={() => openPersonDrawer(m)}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3.5">
                            <div className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                              <Building2 size={14} className="text-slate-400 shrink-0" />
                              <span>{m.name}</span>
                            </div>
                            <span className="text-[10px] text-slate-400 font-mono block truncate max-w-xs">
                              {m.tenant_id}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 capitalize">
                              {m.module || 'standard'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-700 dark:text-slate-300">
                            ₹{m.gross_collected.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-500">
                            ₹{m.platform_commission.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-500">
                            ₹{m.paid_out.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-400">
                            ₹{m.withdrawals_on_hold.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-sm font-extrabold font-mono text-slate-900 dark:text-white">
                              ₹{m.payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                            {m.bank?.account ? (
                              <div className="space-y-0.5">
                                <span className="font-mono font-semibold text-slate-800 dark:text-slate-200 block">
                                  {maskAccount(m.bank.account)}
                                </span>
                                {m.bank.ifsc && (
                                  <span className="text-[10px] font-mono text-slate-400 block">
                                    IFSC: {m.bank.ifsc}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                <AlertTriangle size={10} />
                                Bank details missing
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                            <Button
                              size="sm"
                              disabled={m.payable <= 0 || !m.wallet_id}
                              onClick={() => openPayModal(m)}
                              className="h-8 text-xs font-semibold px-3 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shadow-xs disabled:opacity-40"
                            >
                              <Banknote size={13} />
                              <span>Pay</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* TAB 2: PROFESSIONALS */}
            {activeTab === 'professionals' && (
              <div className="overflow-x-auto">
                {filteredProfessionals.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-xs">
                    No service professionals match the selected filters.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase font-semibold text-[10px] text-slate-400 tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-3">Professional</th>
                        <th className="px-4 py-3">Merchant</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3 text-right">Earned Online</th>
                        <th className="px-4 py-3 text-right">Comm Recoverable</th>
                        <th className="px-4 py-3 text-right">Paid Out</th>
                        <th className="px-4 py-3 text-right">PAYABLE NOW</th>
                        <th className="px-4 py-3">Bank / UPI</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {filteredProfessionals.map(p => (
                        <tr
                          key={p.service_provider_id}
                          onClick={() => openPersonDrawer(p)}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3.5">
                            <span className="font-bold text-slate-900 dark:text-white block">
                              {p.name}
                            </span>
                            <span className="text-[10px] text-slate-400 capitalize">
                              {p.provider_type?.replace('_', ' ') || 'Professional'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300">
                            {p.merchant_name || '—'}
                          </td>
                          <td className="px-4 py-3.5 font-mono text-slate-600 dark:text-slate-300">
                            {p.phone || '—'}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-700 dark:text-slate-300">
                            ₹{p.earned_online.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-rose-500">
                            ₹{p.commission_recoverable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-slate-500">
                            ₹{p.paid_out.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            {p.payable < 0 ? (
                              <div className="flex flex-col items-end">
                                <span className="text-sm font-extrabold font-mono text-rose-600 dark:text-rose-400">
                                  -₹{Math.abs(p.payable).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </span>
                                <span className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">
                                  Recoverable
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm font-extrabold font-mono text-slate-900 dark:text-white">
                                ₹{p.payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                            {p.bank?.upi ? (
                              <span className="font-mono text-slate-800 dark:text-slate-200 block text-[11px]">
                                UPI: {p.bank.upi}
                              </span>
                            ) : p.bank?.account ? (
                              <span className="font-mono text-slate-800 dark:text-slate-200 block">
                                {maskAccount(p.bank.account)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                <AlertTriangle size={10} />
                                Bank details missing
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                            {p.payable > 0 ? (
                              <Button
                                size="sm"
                                onClick={() => openPayModal(p)}
                                className="h-8 text-xs font-semibold px-3 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shadow-xs"
                              >
                                <Banknote size={13} />
                                <span>Pay</span>
                              </Button>
                            ) : (
                              <span className="text-[11px] text-slate-400 italic">No payout due</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* TAB 3: RIDERS / DRIVERS */}
            {activeTab === 'riders' && (
              <div className="overflow-x-auto">
                {filteredRiders.length === 0 ? (
                  <div className="p-16 text-center text-slate-400 space-y-2">
                    <Bike size={32} className="mx-auto text-slate-300 dark:text-slate-700" />
                    <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">No riders yet</h4>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto">
                      Transport & delivery riders will automatically appear here once onboarded with platform wallets.
                    </p>
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase font-semibold text-[10px] text-slate-400 tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-3">Rider Name</th>
                        <th className="px-4 py-3">Merchant</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3 text-right">Earned Online</th>
                        <th className="px-4 py-3 text-right">Comm Recoverable</th>
                        <th className="px-4 py-3 text-right">Paid Out</th>
                        <th className="px-4 py-3 text-right">PAYABLE NOW</th>
                        <th className="px-4 py-3">Bank / UPI</th>
                        <th className="px-4 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {filteredRiders.map(r => (
                        <tr
                          key={r.service_provider_id}
                          onClick={() => openPersonDrawer(r)}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 cursor-pointer"
                        >
                          <td className="px-4 py-3.5 font-bold text-slate-900 dark:text-white">
                            {r.name}
                          </td>
                          <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300">
                            {r.merchant_name || '—'}
                          </td>
                          <td className="px-4 py-3.5 font-mono text-slate-600 dark:text-slate-300">
                            {r.phone || '—'}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono">
                            ₹{r.earned_online.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono text-rose-500">
                            ₹{r.commission_recoverable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right font-mono">
                            ₹{r.paid_out.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-sm font-extrabold font-mono text-slate-900 dark:text-white">
                              ₹{r.payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                            {r.bank?.upi || maskAccount(r.bank?.account) || 'Missing'}
                          </td>
                          <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                            <Button
                              size="sm"
                              disabled={r.payable <= 0}
                              onClick={() => openPayModal(r)}
                              className="h-8 text-xs font-semibold px-3 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                            >
                              Pay
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* TAB 4: GENERAL HISTORY */}
            {activeTab === 'history' && (
              <div className="overflow-x-auto">
                {loadingHistoryTab ? (
                  <div className="p-16 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                    <RefreshCw size={16} className="animate-spin text-primary" />
                    <span>Loading payout history...</span>
                  </div>
                ) : filteredHistory.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-xs">
                    No recorded payouts found.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 uppercase font-semibold text-[10px] text-slate-400 tracking-wider border-b border-slate-200 dark:border-slate-800">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Beneficiary</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3">Method</th>
                        <th className="px-4 py-3">Reference (UTR)</th>
                        <th className="px-4 py-3">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {filteredHistory.map(h => (
                        <tr key={h.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                            {new Date(h.paid_at).toLocaleDateString()} {new Date(h.paid_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900 dark:text-white">
                            {h.name}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 capitalize">
                              {h.beneficiary_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                            ₹{Number(h.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-3 capitalize font-medium text-slate-700 dark:text-slate-300">
                            {h.method?.replace('_', ' ')}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-slate-600 dark:text-slate-400">
                            {h.reference}
                          </td>
                          <td className="px-4 py-3 text-slate-500 max-w-xs truncate">
                            {h.note || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Task 3: [Pay] Modal */}
      {payTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl space-y-4 animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <Banknote size={22} />
                <h3 className="font-bold text-base text-slate-900 dark:text-white">
                  Record Payout: {payTarget.name}
                </h3>
              </div>
              <button
                onClick={() => setPayTarget(null)}
                disabled={recordingPayout}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {/* Payable Summary & Bank Details with Copy */}
            <div className="p-4 bg-slate-50 dark:bg-slate-850 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-slate-500 uppercase font-semibold text-[10px]">Beneficiary Type:</span>
                <span className="font-bold capitalize text-slate-800 dark:text-slate-200">{payTarget.beneficiary_type}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-500 uppercase font-semibold text-[10px]">Payable Balance:</span>
                <span className="text-base font-extrabold font-mono text-emerald-600 dark:text-emerald-400">
                  ₹{payTarget.payable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Unmasked Bank / UPI details with Copy */}
              <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block">
                  Transfer Destination Details
                </span>

                {'bank' in payTarget && payTarget.bank?.upi && (
                  <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-800">
                    <div>
                      <span className="text-[10px] text-slate-400 block">UPI ID</span>
                      <span className="font-mono font-bold text-slate-900 dark:text-white">
                        {payTarget.bank.upi}
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(payTarget.bank!.upi!, 'upi')}
                      className="h-7 px-2 text-xs"
                    >
                      {copiedKey === 'upi' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    </Button>
                  </div>
                )}

                {'bank' in payTarget && payTarget.bank?.account ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-800">
                      <div>
                        <span className="text-[10px] text-slate-400 block">Account Number</span>
                        <span className="font-mono font-bold text-slate-900 dark:text-white">
                          {payTarget.bank.account}
                        </span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopy(payTarget.bank!.account!, 'acc')}
                        className="h-7 px-2 text-xs"
                      >
                        {copiedKey === 'acc' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                      </Button>
                    </div>

                    <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded-lg border border-slate-200 dark:border-slate-800">
                      <div>
                        <span className="text-[10px] text-slate-400 block">IFSC Code</span>
                        <span className="font-mono font-bold text-slate-900 dark:text-white">
                          {payTarget.bank.ifsc || '—'}
                        </span>
                      </div>
                      {payTarget.bank.ifsc && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopy(payTarget.bank!.ifsc!, 'ifsc')}
                          className="h-7 px-2 text-xs"
                        >
                          {copiedKey === 'ifsc' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                        </Button>
                      )}
                    </div>

                    {payTarget.bank.holder && (
                      <div className="text-[11px] text-slate-500">
                        Account Holder: <span className="font-semibold text-slate-700 dark:text-slate-300">{payTarget.bank.holder}</span>
                      </div>
                    )}
                  </div>
                ) : !('bank' in payTarget && payTarget.bank?.upi) ? (
                  <div className="p-2.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-300 text-[11px] flex items-center gap-2">
                    <AlertTriangle size={14} className="shrink-0" />
                    <span>No bank or UPI details on file. Ensure you collect details before transferring.</span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Payout Form */}
            <form onSubmit={handleConfirmPayout} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                {/* Amount */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Amount to Disburse (₹) <span className="text-rose-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="1"
                    max={payTarget.payable}
                    required
                    value={payAmount}
                    onChange={e => setPayAmount(e.target.value)}
                    disabled={recordingPayout}
                    className="h-9 text-xs font-mono font-bold"
                  />
                  <span className="text-[10px] text-slate-400">Max ₹{payTarget.payable.toFixed(2)}</span>
                </div>

                {/* Method */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Transfer Method <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={payMethod}
                    onChange={e => setPayMethod(e.target.value as any)}
                    disabled={recordingPayout}
                    className="w-full h-9 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs"
                  >
                    <option value="bank_transfer">Bank Transfer (NEFT/IMPS/RTGS)</option>
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  UTR / Transaction ID <span className="text-rose-500">*</span>
                </label>
                <Input
                  required
                  placeholder="e.g. UTR-20261011-987654 or UPI txn id"
                  value={payReference}
                  onChange={e => setPayReference(e.target.value)}
                  disabled={recordingPayout}
                  className="h-9 text-xs font-mono"
                />
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Note / Comments (optional)
                </label>
                <Input
                  placeholder="e.g. Weekly settlement or advance payout"
                  value={payNote}
                  onChange={e => setPayNote(e.target.value)}
                  disabled={recordingPayout}
                  className="h-9 text-xs"
                />
              </div>

              {/* Modal Actions */}
              <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPayTarget(null)}
                  disabled={recordingPayout}
                  className="h-9 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={recordingPayout || !payReference.trim()}
                  className="h-9 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 shadow-xs"
                >
                  {recordingPayout ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={13} />
                      Confirm Payout
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task 4: Person Payout History Slide-over Drawer */}
      {drawerPerson && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md h-full shadow-2xl flex flex-col border-l border-slate-200 dark:border-slate-800 animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-850">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                  Payout History
                </span>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {drawerPerson.name}
                </h3>
              </div>
              <button
                onClick={() => setDrawerPerson(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl text-xs space-y-1.5 border border-slate-200 dark:border-slate-700">
                <div className="flex justify-between">
                  <span className="text-slate-400">Current Payable:</span>
                  <span className="font-mono font-bold text-slate-900 dark:text-white">
                    ₹{drawerPerson.payable.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Paid Out:</span>
                  <span className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                    ₹{drawerPerson.paid_out.toFixed(2)}
                  </span>
                </div>
              </div>

              {loadingPersonHistory ? (
                <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center gap-2">
                  <RefreshCw size={16} className="animate-spin text-primary" />
                  <span>Loading payout ledger...</span>
                </div>
              ) : personHistory.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs">
                  No previous payouts recorded for this wallet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    Past Disbursements ({personHistory.length})
                  </h4>
                  {personHistory.map(item => (
                    <div
                      key={item.id}
                      className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 space-y-1.5 shadow-2xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-extrabold text-sm text-emerald-600 dark:text-emerald-400">
                          ₹{Number(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {new Date(item.paid_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                        <span className="capitalize">{item.method?.replace('_', ' ')}</span>
                        <span className="font-mono text-[11px] text-slate-500">Ref: {item.reference}</span>
                      </div>
                      {item.note && (
                        <p className="text-[11px] text-slate-400 italic pt-1 border-t border-slate-100 dark:border-slate-800">
                          {item.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayoutsView;
